import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getSupportDocs, saveSupportDoc, deleteSupportDoc } from '../api';
import { useAuth } from '../context/AuthContext';
import SearchInput from '../components/SearchInput';
import ConfirmDialog from '../components/ConfirmDialog';
import { Svg, Modal } from '../components/PortalUI';
import { SUPPORT_CATEGORIES, SUPPORT_DOCS } from '../data/supportDocs';
import '../components/PortalUI.css';

// What a body may contain: a blank line between paragraphs, "## " for a heading and "- " for a bullet.
function Body({ text }) {
  const blocks = String(text || '').split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  return (
    <div className="pu-article">
      {blocks.map((b, i) => {
        if (b.startsWith('## ')) return <h3 key={i}>{b.slice(3)}</h3>;
        const lines = b.split('\n');
        if (lines.every((l) => l.trim().startsWith('- '))) return <ul key={i}>{lines.map((l, j) => <li key={j}>{l.trim().slice(2)}</li>)}</ul>;
        return <p key={i}>{b}</p>;
      })}
    </div>
  );
}

// Support: our own documentation for the people who run customers. Written in place by NordComply (with the right to).
// editable: only in the Provider portal (NordComply writes the articles); the Tenant portal just reads them.
export default function SupportPage({ editable = false }) {
  const { permissions, isOwner } = useAuth();
  const canWrite = editable && (isOwner || permissions.includes('support'));
  const [stored, setStored] = useState([]);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => { getSupportDocs().then(setStored).catch((err) => setError(err.message)); }, []);
  useEffect(() => { load(); }, [load]);

  // A stored article replaces the placeholder with the same id; the rest of the placeholders stay as they are.
  const docs = useMemo(() => {
    const byId = new Map(SUPPORT_DOCS.map((d) => [d.id, d]));
    stored.forEach((d) => byId.set(d.id, d));
    return [...byId.values()];
  }, [stored]);
  const q = search.trim().toLowerCase();
  const shown = docs.filter((d) => !q || `${d.title} ${d.summary} ${d.body}`.toLowerCase().includes(q));
  const open = docs.find((d) => d.id === openId);

  if (open && open.body) {
    return (
      <div className="pu-page">
        <button type="button" className="pu-back" onClick={() => setOpenId(null)}><Svg name="back" size={13} />Support</button>
        <div className="pu-head">
          <div><span className="pu-kicker">{(SUPPORT_CATEGORIES.find((c) => c.key === open.category) || {}).title}</span><h1>{open.title}</h1>{open.summary && <p>{open.summary}</p>}</div>
          {canWrite && <button type="button" className="pu-btn" onClick={() => setEditing(open)}>Rediger</button>}
        </div>
        <Body text={open.body} />
        {open.updatedAt && <p className="pu-foot">Sidst rettet {new Date(open.updatedAt).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' })}{open.updatedByName ? ` af ${open.updatedByName.split(' ')[0]}` : ''}.</p>}
        {editing && <DocEditor doc={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onRemove={() => { setEditing(null); setRemoving(open); }} />}
        {removing && <ConfirmDialog title="Slet vejledning" message={`Slet "${removing.title}"?`} confirmLabel="Slet" onCancel={() => setRemoving(null)} onConfirm={async () => { await deleteSupportDoc(removing.id); setRemoving(null); setOpenId(null); load(); }} />}
      </div>
    );
  }

  return (
    <div className="pu-page">
      <div className="pu-head">
        <div className="pu-hero">
          <span className="pu-ico pu-ico--l"><Svg name="help" size={24} /></span>
          <div>
            <h1>Support og vejledninger</h1>
            <p>Sådan kommer I i gang, og sådan sikrer I, at det virker hos kunden. Mangler I noget, så skriv til <b>support@nordcomply.dk</b>.</p>
          </div>
        </div>
        {canWrite && <button type="button" className="pu-btn pu-btn--p" onClick={() => setEditing({ category: 'start' })}><Svg name="doc" />Ny vejledning</button>}
      </div>
      {error && <p className="state-error">{error}</p>}
      <div className="pu-bar"><SearchInput value={search} onChange={setSearch} placeholder="Søg i vejledninger..." /></div>
      {shown.length === 0 && <div className="pu-empty"><p>Ingen vejledninger matcher din søgning</p></div>}
      {SUPPORT_CATEGORIES.map((cat) => {
        const list = shown.filter((d) => d.category === cat.key);
        if (list.length === 0) return null;
        return (
          <section key={cat.key} className="pu-doc-sec">
            <h3>{cat.title}<em>{list.length}</em><small>{cat.text}</small></h3>
            <div className="pu-table">
              {list.map((d) => (
                <div
                  key={d.id}
                  className="pu-tr pu-tr--d pu-tr--click"
                  role="button"
                  tabIndex={0}
                  onClick={() => (d.body ? setOpenId(d.id) : canWrite && setEditing(d))}
                  onKeyDown={(e) => { if (e.key === 'Enter') (d.body ? setOpenId(d.id) : canWrite && setEditing(d)); }}
                >
                  <span className="pu-who"><span className={`pu-docic${d.body ? '' : ' is-soon'}`}><Svg name="doc" size={16} /></span><span><b>{d.title}</b><small>{d.summary}</small></span></span>
                  <span>{d.body ? null : <span className="pu-chip pu-chip--neu">{canWrite ? 'Skriv den' : 'Kommer snart'}</span>}</span>
                  <span className="pu-go">{d.body ? <Svg name="chevron" size={15} /> : null}</span>
                </div>
              ))}
            </div>
          </section>
        );
      })}
      {editing && <DocEditor doc={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onRemove={() => { setEditing(null); setRemoving(editing); }} />}
      {removing && <ConfirmDialog title="Slet vejledning" message={`Slet "${removing.title}"?`} confirmLabel="Slet" onCancel={() => setRemoving(null)} onConfirm={async () => { await deleteSupportDoc(removing.id); setRemoving(null); load(); }} />}
    </div>
  );
}

function DocEditor({ doc, onClose, onSaved, onRemove }) {
  const [category, setCategory] = useState(doc.category || 'start');
  const [title, setTitle] = useState(doc.title || '');
  const [summary, setSummary] = useState(doc.summary || '');
  const [body, setBody] = useState(doc.body || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function save(e) {
    e.preventDefault();
    if (!title.trim()) { setError('Skriv en titel.'); return; }
    setSaving(true);
    setError('');
    try {
      await saveSupportDoc({ id: doc.id, category, title, summary, body });
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <Modal size="l" onClose={onClose} busy={saving} head={<div><h2>{doc.id ? 'Ret vejledning' : 'Ny vejledning'}</h2><small>Vises for alle, der bruger Tenant-portalen.</small></div>}>
      <form onSubmit={save}>
        <div className="pu-mb">
          <div className="pu-grid2">
            <div className="pu-col">
              <div className="pu-f"><label>Titel</label><input value={title} maxLength={140} onChange={(e) => setTitle(e.target.value)} disabled={saving} autoFocus /></div>
              <div className="pu-f"><label>Kort beskrivelse</label><input value={summary} maxLength={240} onChange={(e) => setSummary(e.target.value)} disabled={saving} /></div>
              <div className="pu-f">
                <span className="pu-lab">Kategori</span>
                <div className="pu-seg pu-seg--w">{SUPPORT_CATEGORIES.map((c) => (<button key={c.key} type="button" className={category === c.key ? 'is-on' : ''} onClick={() => setCategory(c.key)} disabled={saving}>{c.title}</button>))}</div>
              </div>
              <p className="pu-info"><b>Sådan skriver du</b><span>En tom linje starter et nyt afsnit. Start en linje med ## for en overskrift, og med - for en punktopstilling.</span></p>
            </div>
            <div className="pu-col">
              <div className="pu-f"><label>Indhold</label><textarea className="pu-ta pu-ta--tall" rows={16} value={body} onChange={(e) => setBody(e.target.value)} disabled={saving} /></div>
            </div>
          </div>
          {error && <p className="pu-msg pu-msg--bad">{error}</p>}
        </div>
        <div className="pu-mf">
          {doc.body && <button type="button" className="pu-link pu-link--bad" onClick={onRemove} disabled={saving}>Slet vejledning</button>}
          <span className="pu-sp" />
          <button type="button" className="pu-btn" onClick={onClose} disabled={saving}>Annullér</button>
          <button type="submit" className="pu-btn pu-btn--p" disabled={saving}>{saving ? 'Gemmer…' : 'Gem'}</button>
        </div>
      </form>
    </Modal>
  );
}
